
import { toast } from "sonner";
import { v4 as uuidv4 } from 'uuid';
import { supabase } from "@/integrations/supabase/client";

/**
 * Uploads a file (image or PDF) to Supabase storage
 * @param file The file to upload
 * @param type The type of file ('image' or 'pdf')
 * @returns The public URL of the uploaded file or null if upload failed
 */
export const uploadFile = async (file: File, type: 'image' | 'pdf'): Promise<string | null> => {
  if (!file) return null;
  
  // Check file type
  if (type === 'image' && !file.type.startsWith('image/')) {
    toast.error('Alleen afbeeldingsbestanden zijn toegestaan (.jpg, .png, .gif, etc)');
    return null;
  }
  
  if (type === 'pdf' && file.type !== 'application/pdf') {
    toast.error('Alleen PDF-bestanden zijn toegestaan');
    return null;
  }
  
  try {
    // Generate a unique filename
    const fileExtension = file.name.split('.').pop();
    const fileName = `${uuidv4()}.${fileExtension}`;
    const filePath = `campaign-content/${fileName}`;
    
    console.log('Starting file upload to Supabase:', {
      bucket: 'campaigns',
      path: filePath,
      contentType: file.type
    });
    
    // Upload to Supabase Storage
    const { data: uploadData, error: uploadError } = await supabase
      .storage
      .from('campaigns')
      .upload(filePath, file, {
        contentType: file.type,
        cacheControl: '3600'
      });
      
    if (uploadError) {
      console.error(`Error uploading ${type}:`, uploadError);
      throw uploadError;
    }
    
    console.log('Upload successful:', uploadData);
    
    // Get the public URL for the file
    const { data: urlData } = supabase
      .storage
      .from('campaigns')
      .getPublicUrl(filePath);
      
    if (!urlData || !urlData.publicUrl) {
      throw new Error('Failed to get public URL for uploaded file');
    }
    
    const publicUrl = urlData.publicUrl;
    console.log('File public URL:', publicUrl);
    
    return publicUrl;
  } catch (error) {
    console.error(`Error uploading ${type}:`, error);
    toast.error(`Fout bij het uploaden van ${type === 'image' ? 'afbeelding' : 'PDF'}: ${error instanceof Error ? error.message : 'Onbekende fout'}`);
    return null;
  }
};

/**
 * Creates HTML content for embedding files in the editor
 */
export const createEmbedContent = (publicUrl: string, type: 'image' | 'pdf', fileName: string = ''): string => {
  if (type === 'image') {
    return `\n<img src="${publicUrl}" alt="${fileName}" style="max-width: 100%; margin: 16px 0; border-radius: 8px;" />\n`;
  } else {
    return `\n<iframe src="${publicUrl}" width="100%" height="500" style="border: none; margin: 16px 0; border-radius: 8px;"></iframe>\n`;
  }
};
